import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const TOKEN_KEY = "cp_token";
const USER_KEY = "cp_user";
const SA_TOKEN_KEY = "cp_token_sa";    // backup do token do super admin durante impersonação
const SA_USER_KEY = "cp_user_sa";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [superAdminEmail, setSuperAdminEmail] = useState(null);

  const refreshMe = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data.user);
    setEmpresa(r.data.empresa);
    const imp = Boolean(r.data.user?._impersonated_by);
    setImpersonating(imp);
    setSuperAdminEmail(r.data.user?._impersonated_by || null);
    return r.data;
  };

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    refreshMe().catch(() => {}).finally(() => setLoading(false));
  }, []);

  const login = async (email, password, captcha_id, captcha_answer) => {
    const payload = { email, password };
    if (captcha_id) payload.captcha_id = captcha_id;
    if (captcha_answer !== undefined) payload.captcha_answer = String(captcha_answer);
    const r = await api.post("/auth/login", payload);
    localStorage.setItem(TOKEN_KEY, r.data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(r.data.user));
    // Limpa qualquer resíduo de impersonação prévia
    localStorage.removeItem(SA_TOKEN_KEY);
    localStorage.removeItem(SA_USER_KEY);
    setImpersonating(false);
    setSuperAdminEmail(null);
    setUser(r.data.user);
    const me = await api.get("/auth/me");
    setEmpresa(me.data.empresa);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SA_TOKEN_KEY);
    localStorage.removeItem(SA_USER_KEY);
    setUser(null); setEmpresa(null);
    setImpersonating(false);
    setSuperAdminEmail(null);
    window.location.href = "/login";
  };

  /**
   * startImpersonation: salva o token atual do super admin, troca para o token
   * do cliente, volta à home como admin_empresa. O cliente REAL continua logado
   * em paralelo (JWTs stateless independentes).
   */
  const startImpersonation = async (empresaId) => {
    const r = await api.post(`/superadmin/impersonate/${empresaId}`);
    // Backup do super admin ANTES de trocar o token (senão o axios interceptor envia errado)
    const saToken = localStorage.getItem(TOKEN_KEY);
    const saUser = localStorage.getItem(USER_KEY);
    if (saToken) localStorage.setItem(SA_TOKEN_KEY, saToken);
    if (saUser) localStorage.setItem(SA_USER_KEY, saUser);
    // Troca para o token impersonado
    localStorage.setItem(TOKEN_KEY, r.data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(r.data.user));
    // Re-busca /auth/me para pegar o flag _impersonated_by do JWT
    await refreshMe();
    // Navega para a home do painel do cliente
    window.location.href = "/";
    return r.data;
  };

  /** stopImpersonation: restaura o token do super admin e volta ao painel SaaS. */
  const stopImpersonation = () => {
    const saToken = localStorage.getItem(SA_TOKEN_KEY);
    const saUser = localStorage.getItem(SA_USER_KEY);
    if (!saToken) {
      // sem backup, derruba a sessão impersonada
      logout();
      return;
    }
    localStorage.setItem(TOKEN_KEY, saToken);
    if (saUser) localStorage.setItem(USER_KEY, saUser);
    localStorage.removeItem(SA_TOKEN_KEY);
    localStorage.removeItem(SA_USER_KEY);
    setImpersonating(false);
    setSuperAdminEmail(null);
    window.location.href = "/superadmin";
  };

  return (
    <AuthCtx.Provider value={{
      user, empresa, loading, login, logout,
      impersonating, superAdminEmail,
      startImpersonation, stopImpersonation,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}
